import { SetStateAction, Dispatch } from 'react';
import { useRouter } from 'next/navigation';
import { TradeInputErrorType } from '../../useTradeInput';
import { IUseTradeForm } from '../useTradeForm';
import { OrderOperation } from '@bts-web/data-layer/server';
import { createOrderAction } from '../../../../../../common/gqlActions';
import { processDeepLink } from '../../../../utils/processDeepLink';
import { setWindowHistoryState } from '@bts-web/utils-functions';
import { VERIFY_LINK_KEY } from '../../../../constants';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { datadogErrorHelper } from '../../../../../../common/utils/datadogErrorHelper';
import { getAssetTradeUrl } from '../../../../../../route-builders';
import { ShowModalFunction } from '../../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { SegmentLimitsModal } from '@bts-web/core-features/common';

export const createOrder = async (
  data: {
    assetDetails: IUseTradeForm['assetDetails'];
    amount: string;
    targetPrice: string;
    orderOperation: OrderOperation;
  },
  handlers: {
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    setErrors: Dispatch<SetStateAction<TradeInputErrorType>>;
    processErrors: (gqlResponseErrors?: string[]) => Promise<void>;
    router: ReturnType<typeof useRouter>;
    showModal?: ShowModalFunction;
  },
  flags: {
    feature_hasOrdersEnabled?: boolean;
    feature_showSegmentLimitsError?: boolean;
  } = {},
) => {
  const { feature_hasOrdersEnabled, feature_showSegmentLimitsError } = flags;

  try {
    const response = await createOrderAction({
      assetId: data.assetDetails.id,
      amount: data.amount,
      targetPrice: data.targetPrice,
      operation: data.orderOperation,
    });

    if (response.errors) {
      if (
        feature_hasOrdersEnabled &&
        feature_showSegmentLimitsError &&
        response.data?.createOrder?.__typename === 'CreateTradeOfferError' &&
        (response.data?.createOrder.code ===
          'INVESTMENT_NORMAL_LIMIT_REACHED' ||
          response.data?.createOrder.code ===
            'INVESTMENT_ZERO_LIMIT_REACHED') &&
        handlers.showModal
      ) {
        handlers.showModal(SegmentLimitsModal, {
          type: response.data.createOrder?.code,
          details: response.data.createOrder?.details,
          assetId: data.assetDetails.id,
        });
      }

      handlers.setIsLoading(false);

      handlers.processErrors(response.errors);

      return;
    }

    if (!response.data || !response.data.createOrder) {
      handlers.setIsLoading(false);

      throw new Error('server failure');
    }

    if (response.data.createOrder.__typename === 'OrderSignatureNeeded') {
      const deepLink = response.data.createOrder.deepLink;

      const processedDeepLink = processDeepLink(deepLink);

      const verifyLink = processedDeepLink;

      if (verifyLink) {
        setWindowHistoryState({
          newHistoryState: {
            key: VERIFY_LINK_KEY,
            value: verifyLink,
          },
          searchParams: window.location.search,
          pathname: window.location.pathname || '/',
        });

        return;
      }

      datadogErrorHelper({
        errorMessage: `[ERR][CREATE ORDER] missing 2FA deepLink`,
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });

      handlers.processErrors(['generic_monitored_error_description']);

      return;
    }

    if (response.data.createOrder.__typename === 'Order') {
      handlers.router.push(
        getAssetTradeUrl(data.assetDetails.id, {
          type: 'createOrderRedirect',
          orderType: data.orderOperation,
          orderId: response.data.createOrder.id,
        }),
      );
    }
  } catch (error) {
    handlers.setIsLoading(false);

    handlers.processErrors(['generic_monitored_error_description']);

    datadogErrorHelper({
      errorMessage: `[ERR][CREATE ORDER] ${error}`,
      errorSeverity: DatadogErrorLevels.CRITICAL,
    });
  }
};
